/* eslint-disable no-prototype-builtins */
import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import {
  closeChangePasswordModal,
  closeForgotPasswordModal,
} from 'modules/modal/actions';
import {
  currentUrl,
  forgetAccountsHandoff,
  redirectToAccountsLogin,
} from 'utils/accounts';

import { LOGGED_OUT_SEARCH } from 'utils/return-url';
import { NO_PERMISSION_ERROR } from 'modules/auth/messages';
import SDK from 'utils/sdk';
import { SubmissionError } from 'redux-form';
import { createAbilityForUser } from 'utils/casl/ability-utils';
import { getMeItem } from 'modules/me/selectors';
import { push } from 'connected-react-router';
import { retrieveMe } from 'modules/me/actions';
import { toast } from 'react-toastify';
import {
  changePassword,
  loginUser,
  logoutUser,
  resetPassword,
  resetPasswordRequest,
  showConfirmPassword,
  showNewPassword,
  showPassword,
  verifySession,
} from './actions';

export function* loginUserSaga({ payload: { values: requestBody } }) {
  try {
    yield put(loginUser.request());

    const client = yield call([SDK, 'getClient']);
    const response = yield call([client, 'userLogIn'], null, requestBody);

    if (response.status === 200) {
      const { data } = response;
      const ability = createAbilityForUser(data.permissions);
      yield put(retrieveMe.success(data));
      yield put(
        loginUser.success({ ability, tokenExpiresAt: data.tokenExpiresAt })
      );
      // This tab has a live session again, so a later expiry may hand off to the
      // accounts app instead of dropping straight to the local fallback.
      yield call(forgetAccountsHandoff);
    } else {
      const data = response.response?.data?.message?.toString();
      yield put(loginUser.failure(new SubmissionError({ _error: data })));
    }
  } catch (error) {
    yield put(
      loginUser.failure(new SubmissionError({ _error: NO_PERMISSION_ERROR }))
    );
  } finally {
    yield put(loginUser.fulfill());
  }
}

export function* verifyUserSessionSaga() {
  try {
    yield put(verifySession.request());
    const client = yield call([SDK, 'getClient']);
    const response = yield call([client, 'verifyUserSession']);
    const { data } = response;
    const ability = createAbilityForUser(data.permissions);
    yield put(retrieveMe.success(data));
    yield put(
      verifySession.success({ ability, tokenExpiresAt: data.tokenExpiresAt })
    );
    // The hand-off did its job (or was never needed) — reset the one-shot guard.
    yield call(forgetAccountsHandoff);
  } catch (error) {
    yield put(logoutUser());
    yield put(verifySession.failure());
  } finally {
    yield put(verifySession.fulfill());
  }
}

export function* sessionExpiryWatcherSaga({ payload }) {
  const tokenExpiresAt = payload?.tokenExpiresAt;
  if (!tokenExpiresAt) {
    // tokenExpiresAt missing — session expiry watcher will not run'
    return;
  }

  const expiresAtMs = new Date(tokenExpiresAt).getTime();
  if (Number.isNaN(expiresAtMs)) {
    // tokenExpiresAt is not a valid date
    return;
  }

  const expiresInMs = expiresAtMs - Date.now();

  const { expired } = yield race({
    expired: delay(Math.max(expiresInMs, 0)),
    loggedOut: take([logoutUser.TRIGGER, logoutUser.SUCCESS]),
  });

  if (expired) {
    // Capture the destination first — the redirect below navigates the browser off
    // this origin, and clearing the auth state unmounts the page the user was on.
    const returnUrl = yield call(currentUrl);

    // Local teardown still runs so nothing stale rehydrates. Its `userLogOut` request
    // may be cut short by the navigation, which is fine: an expired token has no live
    // server session left to clear.
    yield put(logoutUser());

    // A misconfigured accounts URL makes redirectToAccountsLogin throw. Don't let that
    // strand the user on the dead, expired page: fall back to the in-app login route.
    try {
      yield call(redirectToAccountsLogin, returnUrl);
    } catch (error) {
      toast.error('Could not reach the sign-in page. Please sign in again.');
      yield put(push('/'));
    }
  }
}

export function* logoutUserSaga({ payload } = {}) {
  // A deliberate sign-out should land on a clean login URL. Navigating away *before*
  // the auth state is cleared unmounts PrivateRoute, so it never gets the chance to
  // record a `return` param for the page being left.
  //
  // A 401 from the API deliberately skips this: it dispatches logoutUser() with no
  // `manual` flag, so PrivateRoute stays mounted long enough to capture the deep link
  // and signing back in resumes it. (Session expiry is handled separately in
  // sessionExpiryWatcherSaga, which captures the URL itself before redirecting.)
  if (payload?.manual) {
    yield put(push({ pathname: '/', search: LOGGED_OUT_SEARCH }));
  }

  try {
    const client = yield call([SDK, 'getClient']);
    yield call([client, 'userLogOut']);

    yield put(logoutUser.success());
  } catch (error) {
    // Local sign-out is best-effort: we clear the local session below regardless of
    // why the server call failed. A 401 just means the session was already gone, so
    // stay quiet; anything else means we couldn't reach the server — say so via a
    // toast (the login form isn't mounted, so a redux `error` would never render).
    if (error?.response?.status !== 401) {
      const data = error?.response?.data?.message?.toString();
      toast.error(
        data || 'Signed out locally, but we could not reach the server.'
      );
    }
    yield put(logoutUser.success());
  } finally {
    yield put(logoutUser.fulfill());
  }
}

export function* changePasswordSaga({ payload: { values: requestBody } }) {
  try {
    yield put(changePassword.request());
    const { currentPassword, newPassword, confirmNewPassword } = requestBody;

    const me = yield select(getMeItem);
    const client = yield call([SDK, 'getClient']);

    const request = {
      currentPassword,
      newPassword,
      confirmNewPassword,
    };

    yield call([client, 'userUpdatePassword'], { id: me.id }, request);
    yield put(closeChangePasswordModal());
    yield put(showPassword(false));
    yield put(showNewPassword(false));
    yield put(showConfirmPassword(false));
    toast.success('Your password has been successfully changed');
    yield put(changePassword.success());
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    yield put(changePassword.failure(new SubmissionError({ _error: data })));
    toast.error(data);
  } finally {
    yield put(changePassword.fulfill());
  }
}

export function* resetPasswordRequestSaga({
  payload: { values: requestBody },
}) {
  try {
    yield put(resetPasswordRequest.request());

    const client = yield call([SDK, 'getClient']);
    yield call([client, 'userResetPasswordRequest'], null, requestBody);
    yield put(closeForgotPasswordModal());
    toast.success('Email sent successfully');
    yield put(resetPasswordRequest.success());
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    yield put(
      resetPasswordRequest.failure(new SubmissionError({ _error: data }))
    );
    toast.error(
      'Your account does not have permission to access this application'
    );
  } finally {
    yield put(resetPasswordRequest.fulfill());
  }
}

export function* resetPasswordSaga({ payload: { values } }) {
  try {
    yield put(resetPassword.request());

    const client = yield call([SDK, 'getClient']);

    yield call([client, 'userResetPassword'], null, values);
    yield put(push('/'));
    yield put(showNewPassword(false));
    yield put(showConfirmPassword(false));
    yield put(resetPassword.success());
    toast.success('Your password has been reset successfully');
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    yield put(resetPassword.failure(new SubmissionError({ _error: data })));
    toast.success(error);
  } finally {
    yield put(resetPassword.fulfill());
  }
}

export default function* authWatch() {
  yield all([
    takeLatest(loginUser.TRIGGER, loginUserSaga),
    takeLatest(logoutUser.TRIGGER, logoutUserSaga),
    takeLatest(changePassword.TRIGGER, changePasswordSaga),
    takeLatest(resetPasswordRequest.TRIGGER, resetPasswordRequestSaga),
    takeLatest(resetPassword.TRIGGER, resetPasswordSaga),
    takeLatest(verifySession.TRIGGER, verifyUserSessionSaga),
    takeLatest(
      [loginUser.SUCCESS, verifySession.SUCCESS],
      sessionExpiryWatcherSaga
    ),
  ]);
}
